import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import { 
  Box, 
  Grid, 
  Typography, 
  Stack, 
  alpha, 
  useTheme, 
  Fade, 
  Paper, 
  Avatar,
  Chip,
} from "@mui/material";
import { 
  Cake, 
  Phone,
  Email,
} from "@mui/icons-material";
import PersonalDetails from "../../../components/Clients/ClientDetails/PersonalDetails";
import Assessments from "../../../components/Clients/ClientDetails/Assessments";
import BodyMapIssue from "../../../components/Clients/ClientDetails/BodyMapIssue";
import CarePlan from "../../../components/Clients/ClientDetails/CarePlan";
import Visits from "../../../components/Clients/ClientDetails/Visits";
import WhatElse from "../../../components/Clients/ClientDetails/WhatElse";
import ConditionsAlerts from "../../../components/Clients/ClientDetails/ConditionsAlerts";
import RiskAssessment from "../../../components/Clients/ClientDetails/RiskAssessment";
import MentalHealth from "../../../components/Clients/ClientDetails/MentalHealth";
import Allergies from "../../../components/Clients/ClientDetails/Allergies";
import Medications from "../../../components/Clients/ClientDetails/Medications";
import { fetchClientStore } from "../../../zustand/client_store";

const CustomAvatar = ({ firstName, lastName, size = 48 }) => {
  const theme = useTheme();
  const getInitials = (first, last) => {
    const firstInitial = first?.charAt(0) || '';
    const lastInitial = last?.charAt(0) || '';
    return `${firstInitial}${lastInitial}`.toUpperCase();
  };

  return (
    <Avatar
      sx={{
        width: size,
        height: size,
        bgcolor: theme.palette.primary.main,
        fontSize: size * 0.4,
        fontWeight: 700,
        color: '#fff',
        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`
      }}
    >
      {getInitials(firstName, lastName)}
    </Avatar>
  );
};

const ClientDetails = () => {
  const { id } = useParams();
  const theme = useTheme();
  const { clientDetails, fetchClientDetails } = fetchClientStore();

  useEffect(() => {
    if (id) {
      fetchClientDetails(id);
    }
  }, [id, fetchClientDetails]);

  const profilePhotoUrl = clientDetails?.profile_photo || 
                          clientDetails?.profile_photo_url || 
                          clientDetails?.avatar;

  const calculateAge = (birthdate) => {
    if (!birthdate) return null;
    const today = new Date();
    const birth = new Date(birthdate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  const age = calculateAge(clientDetails?.birthdate);

  const getPrimaryPhone = () => {
    if (!clientDetails?.phones) return null;
    const primary = clientDetails.phones.find(p => p.preference === 1);
    return primary || clientDetails.phones[0];
  };

  const getPrimaryEmail = () => {
    if (!clientDetails?.emails) return null;
    const primary = clientDetails.emails.find(e => e.preference === 1);
    return primary || clientDetails.emails[0];
  };

  const primaryPhone = getPrimaryPhone();
  const primaryEmail = getPrimaryEmail();

  return (
    <Fade in timeout={800}>
      <Box sx={{ 
        minHeight: "100vh", 
        bgcolor: "#f8fafc",
        p: { xs: 1.5, md: 3 },
        pt: 1
      }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, md: 2.5 },
            mb: 3,
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.divider, 0.06)}`,
            background: "#ffffff",
          }}
        >
          <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ xs: "flex-start", sm: "center" }} spacing={{ xs: 1.5, sm: 2 }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              {profilePhotoUrl ? (
                <Avatar
                  src={profilePhotoUrl}
                  alt={`${clientDetails?.first_name} ${clientDetails?.last_name}`}
                  sx={{ 
                    width: 56, 
                    height: 56,
                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                  }}
                />
              ) : (
                <CustomAvatar 
                  firstName={clientDetails?.first_name} 
                  lastName={clientDetails?.last_name}
                  size={56}
                />
              )}
              <Box>
                <Typography variant="h5" fontWeight={800} sx={{ color: "#1e293b", fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                  {clientDetails?.first_name} {clientDetails?.last_name}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mt: 0.25 }}>
                  {clientDetails?.birthdate && (
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Cake sx={{ fontSize: 13, color: "primary.main" }} />
                      <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                        {clientDetails?.birthdate}
                      </Typography>
                    </Stack>
                  )}
                  {age !== null && (
                    <Chip
                      label={`${age} yrs`}
                      size="small"
                      sx={{
                        height: 18,
                        fontSize: "0.55rem",
                        bgcolor: alpha("#10b981", 0.08),
                        color: "#10b981",
                        fontWeight: 600,
                      }}
                    />
                  )}
                  <Chip
                    label={clientDetails?.is_active ? "Active" : "Inactive"}
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: "0.55rem",
                      bgcolor: clientDetails?.is_active ? "#D1FAE5" : "#FEE2E2",
                      color: clientDetails?.is_active ? "#039855" : "#EF4444",
                      fontWeight: 600,
                    }}
                  />
                  {clientDetails?.nhs_number && (
                    <Chip
                      label={`NHS: ${clientDetails?.nhs_number}`}
                      size="small"
                      sx={{
                        height: 18,
                        fontSize: "0.55rem",
                        bgcolor: alpha("#6366f1", 0.08),
                        color: "#6366f1",
                        fontWeight: 600,
                      }}
                    />
                  )}
                </Stack>
              </Box>
            </Stack>

            <Stack 
              direction="row" 
              spacing={1.5} 
              sx={{ ml: { sm: "auto" }, width: { xs: "100%", sm: "auto" } }}
            >
              {primaryPhone && (
                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ bgcolor: "#f8fafc", px: 1.2, py: 0.5, borderRadius: 1.5 }}>
                  <Phone sx={{ fontSize: 14, color: "text.disabled" }} />
                  <Typography variant="caption" fontWeight={500} color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                    {primaryPhone.number}
                  </Typography>
                </Stack>
              )}
              {primaryEmail && (
                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ bgcolor: "#f8fafc", px: 1.2, py: 0.5, borderRadius: 1.5 }}>
                  <Email sx={{ fontSize: 14, color: "text.disabled" }} />
                  <Typography variant="caption" fontWeight={500} color="text.secondary" sx={{ fontSize: '0.7rem' }} noWrap>
                    {primaryEmail.email}
                  </Typography>
                </Stack>
              )}
            </Stack>
          </Stack>
        </Paper>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6, lg: 4 }}>
            <Stack spacing={2}>
              <SectionWrapper><PersonalDetails /></SectionWrapper>
              <SectionWrapper><Assessments /></SectionWrapper>
              <SectionWrapper><BodyMapIssue /></SectionWrapper>
            </Stack>
          </Grid>

          <Grid size={{ xs: 12, md: 6, lg: 4 }}>
            <Stack spacing={2}>
              {/* <SectionWrapper><Documents /></SectionWrapper> */}
              <SectionWrapper><CarePlan /></SectionWrapper>
              <SectionWrapper><Visits /></SectionWrapper>
              <SectionWrapper><WhatElse /></SectionWrapper>
            </Stack>
          </Grid>

          <Grid size={{ xs: 12, md: 6, lg: 4 }}>
            <Stack spacing={2}>
              <SectionWrapper><ConditionsAlerts /></SectionWrapper>
              <SectionWrapper><RiskAssessment /></SectionWrapper>
              <SectionWrapper><MentalHealth /></SectionWrapper>
              <SectionWrapper><Allergies /></SectionWrapper>
              <SectionWrapper><Medications /></SectionWrapper>
            </Stack>
          </Grid>
        </Grid>
      </Box>
    </Fade>
  );
};

const SectionWrapper = ({ children }) => {
  const theme = useTheme();
  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        border: `1px solid ${alpha(theme.palette.divider, 0.06)}`,
        background: "#ffffff",
        overflow: "hidden",
        transition: "0.2s cubic-bezier(0.4, 0, 0.2, 1)",
        "&:hover": {
          boxShadow: "0 8px 16px rgba(0,0,0,0.04)",
          transform: "translateY(-1px)",
          borderColor: alpha(theme.palette.primary.main, 0.15),
        },
        "& > div": {
          border: "none !important",
          boxShadow: "none !important"
        }
      }}
    >
      {children}
    </Paper>
  );
};

export default ClientDetails;